import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Users as UsersIcon, User, Building2, Send, Link2, Mail, MessageCircle, Copy, Clock, CheckCircle2 } from 'lucide-react';

const EMPTY_FORM = {
  full_name: '', document_number: '', bank_name: '', account_type: 'pix',
  pix_key: '', account_number: '', branch_number: '',
  nationality: '', postal_code: '', address: '', phone: '', email: '',
  relationship: '', transfer_purpose: '', fund_source: '',
};

function RecipientFormFields({ formData, setFormData, t }) {
  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <User className="h-4 w-4" />{t('recipients_personal_info')}
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('recipients_name')} *</Label>
              <Input value={formData.full_name} onChange={e => update('full_name', e.target.value)} required data-testid="recipient-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('recipients_cpf')} *</Label>
              <Input value={formData.document_number} onChange={e => update('document_number', e.target.value)} placeholder="123.456.789-00" required data-testid="document-number-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('recipients_nationality')}</Label>
              <Input value={formData.nationality} onChange={e => update('nationality', e.target.value)} placeholder="Brasil" data-testid="recipient-nationality-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('recipients_postal_code')}</Label>
              <Input value={formData.postal_code} onChange={e => update('postal_code', e.target.value)} placeholder="01000-000" data-testid="recipient-postal-code-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('recipients_address')}</Label>
            <Input value={formData.address} onChange={e => update('address', e.target.value)} data-testid="recipient-address-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('recipients_phone')}</Label>
              <Input value={formData.phone} onChange={e => update('phone', e.target.value)} placeholder="+55 11 99999-9999" data-testid="recipient-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('recipients_email')}</Label>
              <Input value={formData.email} onChange={e => update('email', e.target.value)} type="email" data-testid="recipient-email-input" />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Building2 className="h-4 w-4" />{t('recipients_bank_info')}
        </h3>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('recipients_method')} *</Label>
            <Select value={formData.account_type} onValueChange={v => update('account_type', v)}>
              <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pix">PIX</SelectItem>
                <SelectItem value="bank_account">{t('recipients_bank_account')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.account_type === 'pix' ? (
            <div className="space-y-1.5">
              <Label>{t('recipients_pix')} *</Label>
              <Input value={formData.pix_key} onChange={e => update('pix_key', e.target.value)} placeholder={t('recipients_pix_placeholder')} required data-testid="pix-key-input" />
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label>{t('recipients_bank')} *</Label>
                <Input value={formData.bank_name} onChange={e => update('bank_name', e.target.value)} required data-testid="bank-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t('recipients_branch_number')} *</Label>
                  <Input value={formData.branch_number} onChange={e => update('branch_number', e.target.value)} required data-testid="branch-number-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('recipients_account')} *</Label>
                  <Input value={formData.account_number} onChange={e => update('account_number', e.target.value)} required data-testid="account-number-input" />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Send className="h-4 w-4" />{t('recipients_transfer_info')}
        </h3>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('recipients_relationship')}</Label>
            <Select value={formData.relationship || '_empty'} onValueChange={v => update('relationship', v === '_empty' ? '' : v)}>
              <SelectTrigger data-testid="relationship-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_empty">--</SelectItem>
                <SelectItem value="family">{t('recipients_relationship_family')}</SelectItem>
                <SelectItem value="friend">{t('recipients_relationship_friend')}</SelectItem>
                <SelectItem value="business">{t('recipients_relationship_business')}</SelectItem>
                <SelectItem value="self">{t('recipients_relationship_self')}</SelectItem>
                <SelectItem value="other">{t('recipients_relationship_other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('recipients_transfer_purpose')}</Label>
            <Select value={formData.transfer_purpose || '_empty'} onValueChange={v => update('transfer_purpose', v === '_empty' ? '' : v)}>
              <SelectTrigger data-testid="transfer-purpose-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_empty">--</SelectItem>
                <SelectItem value="living">{t('recipients_purpose_living')}</SelectItem>
                <SelectItem value="education">{t('recipients_purpose_education')}</SelectItem>
                <SelectItem value="business">{t('recipients_purpose_business')}</SelectItem>
                <SelectItem value="investment">{t('recipients_purpose_investment')}</SelectItem>
                <SelectItem value="gift">{t('recipients_purpose_gift')}</SelectItem>
                <SelectItem value="other">{t('recipients_purpose_other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('recipients_fund_source')}</Label>
            <Select value={formData.fund_source || '_empty'} onValueChange={v => update('fund_source', v === '_empty' ? '' : v)}>
              <SelectTrigger data-testid="fund-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_empty">--</SelectItem>
                <SelectItem value="salary">{t('recipients_fund_salary')}</SelectItem>
                <SelectItem value="savings">{t('recipients_fund_savings')}</SelectItem>
                <SelectItem value="business">{t('recipients_fund_business')}</SelectItem>
                <SelectItem value="investment">{t('recipients_fund_investment')}</SelectItem>
                <SelectItem value="other">{t('recipients_fund_other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecipientCard({ recipient, t, onEdit, onDelete }) {
  const relationshipLabel = recipient.relationship ? t(`recipients_relationship_${recipient.relationship}`) : null;
  const purposeLabel = recipient.transfer_purpose ? t(`recipients_purpose_${recipient.transfer_purpose}`) : null;
  const fundLabel = recipient.fund_source ? t(`recipients_fund_${recipient.fund_source}`) : null;

  return (
    <div className="bg-card border border-border/50 rounded-2xl p-6 hover:shadow-md smooth-transition" data-testid={`recipient-card-${recipient.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-primary mb-1">{recipient.full_name}</h3>
          <p className="text-sm text-muted-foreground">CPF/CNPJ: {recipient.document_number}</p>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(recipient)} className="text-muted-foreground hover:text-primary" data-testid={`edit-recipient-${recipient.id}`}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(recipient.id)} className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-recipient-${recipient.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('recipients_method')}:</span>
          <span className="font-medium">{recipient.account_type === 'pix' ? 'PIX' : t('recipients_bank_account')}</span>
        </div>
        {recipient.account_type === 'pix' ? (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('recipients_pix')}:</span>
            <span className="font-mono text-xs">{recipient.pix_key}</span>
          </div>
        ) : (
          <>
            {recipient.bank_name && <div className="flex items-center gap-2"><span className="text-muted-foreground">{t('recipients_bank')}:</span><span>{recipient.bank_name}</span></div>}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">{t('recipients_branch')}/{t('recipients_account')}:</span>
              <span className="font-mono text-xs">{recipient.branch_number} / {recipient.account_number}</span>
            </div>
          </>
        )}

        {(recipient.nationality || recipient.address || recipient.phone || recipient.email || relationshipLabel || purposeLabel || fundLabel) && (
          <div className="border-t border-border/50 mt-3 pt-3 space-y-1.5">
            {recipient.nationality && <div className="flex items-center gap-2"><span className="text-muted-foreground">{t('recipients_nationality')}:</span><span>{recipient.nationality}</span></div>}
            {recipient.postal_code && <div className="flex items-center gap-2"><span className="text-muted-foreground">{t('recipients_postal_code')}:</span><span>{recipient.postal_code}</span></div>}
            {recipient.address && <div className="flex items-center gap-2"><span className="text-muted-foreground">{t('recipients_address')}:</span><span>{recipient.address}</span></div>}
            {recipient.phone && <div className="flex items-center gap-2"><span className="text-muted-foreground">{t('recipients_phone')}:</span><span>{recipient.phone}</span></div>}
            {recipient.email && <div className="flex items-center gap-2"><span className="text-muted-foreground">{t('recipients_email')}:</span><span>{recipient.email}</span></div>}
            {relationshipLabel && <div className="flex items-center gap-2"><span className="text-muted-foreground">{t('recipients_relationship')}:</span><span>{relationshipLabel}</span></div>}
            {purposeLabel && <div className="flex items-center gap-2"><span className="text-muted-foreground">{t('recipients_transfer_purpose')}:</span><span>{purposeLabel}</span></div>}
            {fundLabel && <div className="flex items-center gap-2"><span className="text-muted-foreground">{t('recipients_fund_source')}:</span><span>{fundLabel}</span></div>}
          </div>
        )}
      </div>
    </div>
  );
}

function ShareButtons({ link, recipientName, t }) {
  const message = t('invite_share_message', { name: recipientName, link });

  const copyLink = () => {
    navigator.clipboard.writeText(link);
    toast.success(t('invite_copied'));
  };

  const shareEmail = () => {
    window.open(`mailto:?subject=${encodeURIComponent('PRC Remit - ' + t('invite_form_title'))}&body=${encodeURIComponent(message)}`);
  };

  const shareLine = () => {
    window.open(`https://line.me/R/share?text=${encodeURIComponent(message)}`);
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`);
  };

  return (
    <div className="space-y-3 min-w-0">
      <p className="text-sm font-medium text-muted-foreground">{t('invite_share_title')}</p>
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={shareEmail} className="gap-2" data-testid="share-email-btn">
          <Mail className="h-4 w-4" />{t('invite_share_email')}
        </Button>
        <Button variant="outline" size="sm" onClick={shareLine} className="gap-2 text-green-600 border-green-200 hover:bg-green-50" data-testid="share-line-btn">
          <MessageCircle className="h-4 w-4" />{t('invite_share_line')}
        </Button>
        <Button variant="outline" size="sm" onClick={shareWhatsApp} className="gap-2 text-green-700 border-green-200 hover:bg-green-50" data-testid="share-whatsapp-btn">
          <MessageCircle className="h-4 w-4" />{t('invite_share_whatsapp')}
        </Button>
        <Button variant="outline" size="sm" onClick={copyLink} className="gap-2" data-testid="share-copy-btn">
          <Copy className="h-4 w-4" />{t('invite_share_copy')}
        </Button>
      </div>
      <div className="flex items-center gap-2 bg-secondary/50 rounded-lg px-3 py-2 min-w-0 overflow-hidden">
        <Link2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-xs font-mono text-muted-foreground truncate">{link}</span>
        <Button variant="ghost" size="sm" onClick={copyLink} className="h-7 px-2 flex-shrink-0">
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function InvitationCard({ invite, t, onDelete }) {
  const isCompleted = invite.status === 'completed';
  return (
    <div className={`bg-card border rounded-xl p-4 ${isCompleted ? 'border-green-200' : 'border-border/50'}`} data-testid={`invitation-card-${invite.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isCompleted ? (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          ) : (
            <Clock className="h-4 w-4 text-amber-500" />
          )}
          <span className="font-medium text-primary">{invite.recipient_name}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${isCompleted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {isCompleted ? t('invite_completed') : t('invite_pending')}
          </span>
        </div>
        {!isCompleted && (
          <Button variant="ghost" size="icon" onClick={() => onDelete(invite.id)} className="text-muted-foreground hover:text-destructive h-8 w-8" data-testid={`delete-invite-${invite.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      {invite.recipient_email && (
        <p className="text-sm text-muted-foreground mb-2">{invite.recipient_email}</p>
      )}
      {!isCompleted && (
        <ShareButtons link={invite.invite_link} recipientName={invite.recipient_name} t={t} />
      )}
    </div>
  );
}

export default function RecipientsPage() {
  const { t } = useLanguage();
  const [tab, setTab] = useState('list');
  const [recipients, setRecipients] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [inviteForm, setInviteForm] = useState({ recipient_name: '', recipient_email: '' });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [recRes, invRes] = await Promise.all([
        api.getRecipients(),
        api.getInvitations(),
      ]);
      setRecipients(recRes.data);
      setInvitations(invRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.createRecipient(formData);
      toast.success(t('recipients_added'));
      setAddDialogOpen(false);
      setFormData({ ...EMPTY_FORM });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('recipients_add_fail'));
    }
  };

  const openEdit = (recipient) => {
    setEditingId(recipient.id);
    setFormData({
      full_name: recipient.full_name || '', document_number: recipient.document_number || '',
      bank_name: recipient.bank_name || '', account_type: recipient.account_type || 'pix',
      pix_key: recipient.pix_key || '', account_number: recipient.account_number || '',
      branch_number: recipient.branch_number || '', nationality: recipient.nationality || '',
      postal_code: recipient.postal_code || '', address: recipient.address || '',
      phone: recipient.phone || '', email: recipient.email || '',
      relationship: recipient.relationship || '', transfer_purpose: recipient.transfer_purpose || '',
      fund_source: recipient.fund_source || '',
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.updateRecipient(editingId, formData);
      toast.success(t('recipients_updated'));
      setEditDialogOpen(false);
      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('recipients_update_fail'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('recipients_delete_confirm'))) return;
    try {
      await api.deleteRecipient(id);
      toast.success(t('recipients_deleted'));
      fetchAll();
    } catch (error) {
      toast.error(t('recipients_delete_fail'));
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.recipient_name) return;
    setInviteSending(true);
    try {
      const res = await api.createInvitation(inviteForm);
      setInviteResult(res.data);
      toast.success(t('invite_success'));
      if (inviteForm.recipient_email) toast.success(t('invite_email_sent'));
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invite_fail'));
    } finally {
      setInviteSending(false);
    }
  };

  const handleDeleteInvite = async (id) => {
    if (!window.confirm(t('invite_delete_confirm'))) return;
    try {
      await api.deleteInvitation(id);
      toast.success(t('invite_deleted'));
      fetchAll();
    } catch {}
  };

  const pendingCount = invitations.filter(i => i.status === 'pending').length;

  return (
    <DashboardLayout>
      <div data-testid="recipients-page">
        <div className="mb-6">
          <div className="mb-4">
            <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-1">{t('recipients_title')}</h1>
            <p className="text-sm text-muted-foreground">{t('recipients_subtitle')}</p>
          </div>
          <div className="flex gap-2">
            {/* Invite Dialog */}
            <Dialog open={inviteDialogOpen} onOpenChange={(open) => { setInviteDialogOpen(open); if (!open) { setInviteForm({ recipient_name: '', recipient_email: '' }); setInviteResult(null); } }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full gap-2 text-xs sm:text-sm" data-testid="invite-recipient-button">
                  <Link2 className="h-4 w-4" />{t('invite_btn')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg overflow-hidden mx-4 sm:mx-auto">
                <DialogHeader>
                  <DialogTitle>{t('invite_title')}</DialogTitle>
                  <DialogDescription>{t('invite_subtitle')}</DialogDescription>
                </DialogHeader>
                {!inviteResult ? (
                  <form onSubmit={handleInvite} className="mt-4 space-y-4" data-testid="invite-form">
                    <div className="space-y-1.5">
                      <Label>{t('invite_name')} *</Label>
                      <Input value={inviteForm.recipient_name} onChange={e => setInviteForm(p => ({ ...p, recipient_name: e.target.value }))} required data-testid="invite-name-field" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('invite_email')} <span className="text-xs text-muted-foreground">{t('invite_email_optional')}</span></Label>
                      <Input type="email" value={inviteForm.recipient_email} onChange={e => setInviteForm(p => ({ ...p, recipient_email: e.target.value }))} data-testid="invite-email-field" />
                    </div>
                    <Button type="submit" disabled={inviteSending} className="w-full rounded-full" data-testid="invite-send-btn">
                      {inviteSending ? t('invite_sending') : t('invite_send')}
                    </Button>
                  </form>
                ) : (
                  <div className="mt-4 space-y-4 min-w-0" data-testid="invite-result">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                      <p className="font-medium text-green-800">{t('invite_success')}</p>
                    </div>
                    <ShareButtons link={inviteResult.invite_link} recipientName={inviteForm.recipient_name} t={t} />
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Add Dialog */}
            <Dialog open={addDialogOpen} onOpenChange={(open) => { setAddDialogOpen(open); if (!open) setFormData({ ...EMPTY_FORM }); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-full text-xs sm:text-sm" data-testid="add-recipient-button">
                  <Plus className="h-4 w-4 mr-1" />{t('recipients_add')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('recipients_new_title')}</DialogTitle>
                  <DialogDescription>{t('recipients_new_desc')}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAdd} className="mt-4" data-testid="add-recipient-form">
                  <RecipientFormFields formData={formData} setFormData={setFormData} t={t} />
                  <Button type="submit" className="w-full rounded-full mt-6" data-testid="submit-recipient-button">
                    {t('recipients_add')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-secondary/50 rounded-lg w-fit mb-6" data-testid="recipient-tabs">
          <Button size="sm" variant={tab === 'list' ? 'default' : 'ghost'} className="h-8 text-sm px-4" onClick={() => setTab('list')} data-testid="tab-list">
            {t('invite_tab_list')} ({recipients.length})
          </Button>
          <Button size="sm" variant={tab === 'invitations' ? 'default' : 'ghost'} className="h-8 text-sm px-4" onClick={() => setTab('invitations')} data-testid="tab-invitations">
            {t('invite_tab_invitations')}
            {pendingCount > 0 && <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{pendingCount}</span>}
          </Button>
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingId(null); setFormData({ ...EMPTY_FORM }); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('recipients_edit_title')}</DialogTitle>
              <DialogDescription>{t('recipients_edit_desc')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEdit} className="mt-4" data-testid="edit-recipient-form">
              <RecipientFormFields formData={formData} setFormData={setFormData} t={t} />
              <Button type="submit" className="w-full rounded-full mt-6" data-testid="save-recipient-button">
                {t('recipients_save')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : tab === 'list' ? (
          recipients.length === 0 ? (
            <div className="text-center py-20">
              <UsersIcon className="h-16 w-16 text-muted-foreground mx-auto mb-6 opacity-50" />
              <h3 className="font-heading text-xl font-semibold text-primary mb-2">{t('recipients_none')}</h3>
              <p className="text-muted-foreground mb-6">{t('recipients_add_first')}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" size="sm" onClick={() => setInviteDialogOpen(true)} className="gap-2" data-testid="empty-invite-btn">
                  <Link2 className="h-4 w-4" />{t('invite_btn')}
                </Button>
                <Button size="sm" onClick={() => setAddDialogOpen(true)} data-testid="empty-add-recipient-button">
                  <Plus className="h-4 w-4 mr-1" />{t('recipients_add')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="recipients-list">
              {recipients.map((recipient) => (
                <RecipientCard key={recipient.id} recipient={recipient} t={t} onEdit={openEdit} onDelete={handleDelete} />
              ))}
            </div>
          )
        ) : (
          invitations.length === 0 ? (
            <div className="text-center py-20">
              <Link2 className="h-16 w-16 text-muted-foreground mx-auto mb-6 opacity-50" />
              <h3 className="font-heading text-xl font-semibold text-primary mb-2">{t('invite_empty')}</h3>
              <Button variant="outline" onClick={() => setInviteDialogOpen(true)} className="gap-2 mt-4">
                <Link2 className="h-4 w-4" />{t('invite_btn')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="invitations-list">
              {invitations.map((invite) => (
                <InvitationCard key={invite.id} invite={invite} t={t} onDelete={handleDeleteInvite} />
              ))}
            </div>
          )
        )}
      </div>
    </DashboardLayout>
  );
}
